import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface SegmentedOption {
  /** Valor emitido quando o segmento é escolhido. */
  value: any;
  /** Chave i18n do rótulo (traduzida no template). Em segmentos-ícone, vira o
   *  aria-label/tooltip (não é exibido). */
  label: string;
  /**
   * Material Symbol (ligatura) do segmento. Se presente, o segmento vira ÍCONE
   * (o `label` traduzido é usado só como aria-label/tooltip). Se ausente, mostra o
   * texto do `label` como antes.
   */
  icon?: string;
  /**
   * SVG inline (markup `<svg>…</svg>`) para quando nenhum Material Symbol descreve
   * o conceito. Tem precedência sobre `icon`. Trata-se de conteúdo INTERNO/estático
   * (nunca entrada de usuário) — sanitizado e cacheado por `safeSvg`. O `label`
   * traduzido segue como aria-label/tooltip, como nos segmentos-ícone.
   */
  svg?: string;
}

/**
 * Connected button group (segmented) — escolha entre opções OPOSTAS/excludentes,
 * em HTML/CSS puro (diretriz: não usar switch nem mat-button-toggle). Reutiliza
 * o estilo criado para o toggle "Ativas/Todas" da página Eventos. Ver
 * [[feedback_segmented_not_switch]].
 */
@Component({
  selector: 'app-segmented-control',
  templateUrl: './segmented-control.component.html',
  styleUrls: ['./segmented-control.component.scss']
})
export class SegmentedControlComponent {
  @Input() options: SegmentedOption[] = [];
  @Input() value: any;
  @Input() disabled = false;
  /** Chave i18n do rótulo acessível do grupo. */
  @Input() ariaLabel: string;
  /**
   * Indicador DESLIZANTE: em vez de o fundo do segmento ativo saltar de um para
   * o outro, um único "thumb" cinza desliza entre eles (transição no `transform`).
   * Opt-in — o padrão continua instantâneo (ver [[feedback_segmented_not_switch]]).
   * Assume segmentos de largura igual (caso típico: só ícones). Requer que o
   * segmento ativo esteja em `options`; senão o thumb repousa no primeiro.
   */
  @Input() sliding = false;

  @Output() valueChange = new EventEmitter<any>();

  constructor(private sanitizer: DomSanitizer) {}

  /** Índice do segmento ativo (0 se o valor não casar) — posiciona o thumb. */
  get activeIndex(): number {
    const i = this.options.findIndex(o => o.value === this.value);
    return i < 0 ? 0 : i;
  }

  // Cache de SVGs sanitizados: o template chama `safeSvg` a cada CD; sem o cache,
  // cada chamada criaria um novo SafeHtml → o `[innerHTML]` re-renderizaria à toa.
  private readonly svgCache = new Map<string, SafeHtml>();
  safeSvg(svg: string): SafeHtml {
    let safe = this.svgCache.get(svg);
    if (!safe) {
      safe = this.sanitizer.bypassSecurityTrustHtml(svg);
      this.svgCache.set(svg, safe);
    }
    return safe;
  }

  select(value: any): void {
    if (value === this.value) { return; }
    this.valueChange.emit(value);
  }
}
